import { Component, computed, effect, inject, signal } from "@angular/core"
import { httpResource } from "@angular/common/http"
import { Router } from "@angular/router"
import { toSignal } from "@angular/core/rxjs-interop"
import { catchError, of } from "rxjs"
import { ToastService } from "src/app/services"
import { Category, CategoryTypes, Column, PagedPerk, TableLazyLoadEvent } from "../../models"
import { CategoryService, PerksService } from "../../services"
import { ColumnFilter, toPagedParams } from "../../helpers/TablePaging"
import { TableComponent } from "../../components/table/table.component"

@Component({
    selector: "valben-perks-table",
    templateUrl: "./perks-table.page.html",
    styles: [`
        :host {
            width: 100%;
            flex: 1 1 auto;
            min-height: 0;
            display: flex;
            flex-direction: column;
        }
    `],
    standalone: true,
    imports: [TableComponent],
})
export class PerksTablePage {
    private _perksService = inject(PerksService)
    private _messageService = inject(ToastService)
    private _router = inject(Router)

    cols: Column[] = [
        { field: "names", subField: "hr", header: "Naslov", bodyType: "langs", filterType: "text", filterMode: "contains", pairWith: "descriptions" },
        { field: "descriptions", subField: "hr", header: "Opis", bodyType: "langs", filterType: "text", filterMode: "contains" },
        { field: "createdByUser", header: "Kreiranje", bodyType: "text", filterType: "text", filterMode: "contains", pairWith: "modifiedByUser" },
        { field: "modifiedByUser", header: "Zadnja izmjena", bodyType: "text", filterType: "text", filterMode: "contains", prefix: "↳" },
        { field: "createdDate", header: "Datum kreiranja", bodyType: "datetime", filterType: "date", filterMode: "dateIs", sortable: true, pairWith: "modifiedDate" },
        { field: "modifiedDate", header: "Datum izmjene", bodyType: "datetime", filterType: "date", filterMode: "dateIs", sortable: true },
        { field: "activeFrom", header: "Aktivan od", bodyType: "date", filterType: "date", filterMode: "dateIs", pairWith: "activeTo" },
        { field: "activeTo", header: "Aktivan do", bodyType: "date", filterType: "date", filterMode: "dateIs", prefix: "→" },
        { field: "isActive", header: "Aktivan", bodyType: "boolean", filterType: "boolean", sortable: true, trueText: "Aktivna", falseText: "Neaktivna", pairWith: "isDraft" },
        { field: "isDraft", header: "Skica", bodyType: "boolean", filterType: "boolean", sortable: true, trueText: "Skica", falseText: "Bez skice" },
        { field: "version", header: "Verzija", bodyType: "number", filterType: "numeric", sortable: true, prefix: "v" },
        { field: "imageUrl", header: "Slika", bodyType: "file", icon: "image" },
        { field: "categoryNames", header: "Kategorije", bodyType: "modal", filterType: "cats", filterMode: "inArr", icon: "sell" },
        { field: "locationNames", header: "Lokacije", bodyType: "modal", filterType: "locs", filterMode: "inArr", icon: "place" },
    ]

    private _pageRequest = signal<TableLazyLoadEvent | undefined>(undefined)

    private _allCategories = toSignal(
        inject(CategoryService)
            .GetAllCached()
            .pipe(catchError(() => of([] as Category[])))
    )

    private _categories = computed(() => (this._allCategories() ?? []).filter(c => c.type === CategoryTypes.Perk))

    perks = httpResource<PagedPerk>(() => {
        const event = this._pageRequest()
        const categories = this._allCategories()
        if (!event || categories === undefined) return undefined

        const resolveCats = (names: string[]): ColumnFilter | null => {
            const ids = this._categories()
                .filter(c => names.includes(c.names?.["hr"]))
                .map(c => c.masterId)
            return ids.length ? { field: "categoryIds", value: ids.join(","), matchMode: "in" } : null
        }

        return this._perksService.PagedRequest(toPagedParams(event, this.cols, resolveCats))
    })

    tableValues = computed(() =>
        (this.perks.value()?.items ?? []).map(perk => ({
            ...perk,
            createdDate: new Date(perk.createdDate),
            modifiedDate: new Date(perk.modifiedDate),
            activeFrom: perk.activeFrom ? new Date(perk.activeFrom) : null,
            activeTo: perk.activeTo ? new Date(perk.activeTo) : null,
        }))
    )

    totalRecords = computed(() => this.perks.value()?.totalCount ?? 0)
    loading = computed(() => this.perks.isLoading())

    refresh = () => this.perks.reload()

    constructor() {
        effect(() => {
            if (!this.perks.error()) return
            this._messageService.add({
                severity: "error",
                summary: "Greška",
                detail: "Došlo je do pogreške prilikom dohvata pogodnosti.",
                life: 10000,
            })
        })
    }

    loadPage(event: TableLazyLoadEvent): void {
        setTimeout(() => this._pageRequest.set(event))
    }

    add(): void {
        this._router.navigate(["/perks/add"])
    }

    edit(masterId: string): void {
        this._router.navigate(["/perks/edit", masterId])
    }
}
